import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import Flex from "components/Flex";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { empty, nonEmpty } from "helpers/coll";
import { backend, IntakeError, UpsertFirmResult } from "Backend";
import { Checkmark, ExcelIcon } from "components/Icons";
import WarningIcon from "@mui/icons-material/Warning";
import { Config } from "../../Config";
import LoadingSpinner from "components/LoadingSpinner";
import { P } from "theme/typography";
import LoadingPattern from "components/LoadingPattern";
import theme from "theme";
import { useSnackbar } from "notistack";

const FirmTabularImport = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [uploading, setUploading] = useState<boolean>(false);
  const [file, setFile] = React.useState<File | undefined>();
  const [errors, setErrors] = useState<IntakeError[]>([]);
  const [successes, setSuccesses] = useState<UpsertFirmResult[]>([]);
  const errorState = nonEmpty(errors);
  const successState = empty(errors) && nonEmpty(successes);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const closeDialog = () => setDialogOpen(false);
  const resetResults = () => {
    setFile(undefined);
    setErrors([]);
    setSuccesses([]);
  };
  const handleDialogClose = () => {
    closeDialog();
    resetResults();
  };
  const [upserting, setUpserting] = React.useState(false);
  const [upsertResults] = React.useState<UpsertFirmResult[]>([]);
  const insertedResults = React.useMemo(
    () => upsertResults.filter((ur) => nonEmpty(ur.actions) && ur.actions[0] === "inserted"),
    [upsertResults]
  );
  const updatedResults = React.useMemo(
    () => upsertResults.filter((ur) => nonEmpty(ur.actions) && ur.actions[0] === "updated_fields"),
    [upsertResults]
  );

  const handleUnexpected = (err: Error) => {
    console.error(err);
    enqueueSnackbar("An unexpected error occured.", { variant: "error" });
  };

  const doImport = () => {
    if (file) {
      setUpserting(true);
      backend
        .upsertFirmsFromFile(file, false)
        .then((response) => {
          setErrors(response.failures);
          setSuccesses(response.successes);
        })
        .catch(handleUnexpected)
        .finally(() => {
          setDialogOpen(false);
          setUpserting(false);
        });
    }
  };

  const onFile = (evt: React.ChangeEvent<HTMLInputElement>) => {
    resetResults();
    const file = evt.target.files?.[0];

    if (file) {
      setFile(file);
      setUploading(true);
      backend
        .upsertFirmsFromFile(file, true)
        .then((response) => {
          setErrors(response.failures);
          setSuccesses(response.successes);
        })
        .catch(handleUnexpected)
        .finally(() => setUploading(false));
    }
  };

  const instructions = (
    <Box>
      <P variant="h5" fontSize={24} sx={{ mb: 1 }}>
        Import Firms
      </P>
      <P variant="body2" sx={{ mb: 2, display: "block" }}>
        To prevent import errors, Pattern recommends
        <Box component="span" sx={{ display: "inline-block", mx: 1 }}>
          <ExcelIcon size={24} />
          <Link
            sx={{
              ...theme.mixins.linkLike,
            }}
            href={Config.firmIntakeTemplateUrl}
            target="_blank">
            downloading the Firm Import Excel template
          </Link>
        </Box>
        and populating it with data. This ensures that column names will be correct. Pattern only recognizes the exact
        column names that are contained in the template. After uploading, the screen will display a list of rows with
        any errors, and steps needed to correct those errors.
      </P>
      <P variant="body2" sx={{ mb: 2, display: "block" }}>
        This import will:
      </P>
      <P sx={{ mb: 2, ml: 2, display: "block" }}>
        <P variant="body2">
          1. Create new firms for any rows in the spreadsheet where the <code>law_firm_id</code> does not have a
          matching firm in Pattern
        </P>
        <P variant="body2">
          2. Update firm data for any rows in the spreadsheet where the <code>law_firm_id</code> DOES have a matching
          firm in Pattern. For example, you can update an existing firm&lsquo;s name, attorney, address, phone number,
          or litigation name. This means you are free to include the same firms that you&lsquo;ve already uploaded in
          the past without having to manage or worry about what firms you have or have not uploaded.
        </P>
      </P>
    </Box>
  );

  return (
    <Card sx={{ width: "100%" }}>
      {empty(errors) && empty(successes) && !uploading && (
        <>
          <CardContent>{instructions}</CardContent>
          <CardActions sx={{ justifyContent: "center" }}>
            <Button id="tabularimport_choose_file" component="label" color="primary" variant="contained">
              Choose CSV or Excel File
              <input
                type="file"
                autoComplete="off"
                tabIndex={-1}
                accept="text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                style={{ display: "none" }}
                onChange={onFile}
              />
            </Button>
          </CardActions>
        </>
      )}
      {uploading && (
        <CardContent sx={{ display: "flex", justifyContent: "center" }}>
          <Box
            sx={{
              height: "500px",
              display: "flex",
              justifyContent: "center",
              flexDirection: "column",
            }}>
            <LoadingPattern />
          </Box>
        </CardContent>
      )}
      {errorState && (
        <>
          <CardContent>
            <Flex p={1} justifyContent="center" alignItems="center">
              <P>
                File: <b>{file?.name}</b>
              </P>
              <Flex p={2} flexDirection="row" justifyContent="center" alignItems="center">
                <WarningIcon />
                <P>
                  <b>{errors.length}</b> {errors.length > 1 ? "firms have" : "firm has"} at least 1 issue. Please
                  correct the issues below, then load the file again
                </P>
              </Flex>
            </Flex>
            <Box sx={{ overflowY: "auto", maxHeight: "450px" }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell>Errors</TableCell>
                    <TableCell>Raw Row</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {errors.map((error) => (
                    <TableRow key={error.lineNumber}>
                      <TableCell>ROW {error.lineNumber + 1}</TableCell>
                      <TableCell>{error.message}</TableCell>
                      <TableCell>
                        {Object.entries(error.rawRecord || {})
                          .sort((a, b) => a[0].localeCompare(b[0]))
                          .map(([k, v]) => {
                            return (
                              <span key={k}>
                                <span style={{ fontSize: "0.5rem", fontWeight: "bold" }}>{k}: </span>
                                <span style={{ fontSize: "0.5rem" }}>{v}, </span>
                              </span>
                            );
                          })}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </CardContent>
          <CardActions sx={{ justifyContent: "space-between" }}>
            <Button color="secondary" onClick={resetResults}>
              Back to instructions
            </Button>
            <Button color="secondary" component="label">
              Load Again
              <input
                type="file"
                autoComplete="off"
                tabIndex={-1}
                accept="text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                style={{ display: "none" }}
                onChange={onFile}
              />
            </Button>
          </CardActions>
        </>
      )}
      {successState && (
        <>
          <CardContent>
            <Flex gap={1} flexDirection="row" alignItems="center" justifyContent="center">
              <Checkmark />
              <P>{successes.length} firms are ready for processing.</P>
            </Flex>
            <Box
              sx={{
                display: "flex",
                overflowY: "auto",
                maxHeight: "450px",
                width: "100%",
                alignItems: "center",
                justifyContent: "center",
              }}>
              <Table stickyHeader size="small" sx={{ maxWidth: "400px" }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Firm Name</TableCell>
                    <TableCell>External Id</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {successes.map((result) => (
                    <TableRow key={result.externalId}>
                      <TableCell>{result.name}</TableCell>
                      <TableCell>{result.externalId}</TableCell>
                      <TableCell>{result.actions[0] ?? ""}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </CardContent>
          <CardActions sx={{ justifyContent: "flex-end" }}>
            <Button onClick={resetResults} color="primary" sx={{ mr: 2 }}>
              Cancel
            </Button>
            <Button
              id="tabularimport_create_firms"
              onClick={doImport}
              color="primary"
              variant="contained"
              startIcon={upserting ? <LoadingSpinner size={24} /> : <CloudUploadIcon />}>
              Process {successes.length} Firms
            </Button>
          </CardActions>
        </>
      )}
      <Dialog open={dialogOpen} onClose={handleDialogClose}>
        <DialogTitle>Success!</DialogTitle>
        <DialogContent>
          <DialogContentText>Firm upload complete</DialogContentText>
          {nonEmpty(upsertResults) && (
            <DialogContentText>{insertedResults.length} firms were created.</DialogContentText>
          )}
          {nonEmpty(upsertResults) && (
            <DialogContentText>{updatedResults.length} firms were updated.</DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default FirmTabularImport;
